import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, CheckCircle, XCircle } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";
import Footer from "../components/Footer";
import PasswordStrengthIndicator from "../components/PasswordStrengthIndicator";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Check if password meets all requirements
  const isPasswordValid = () => {
    return password.length >= 8 &&
           /[A-Z]/.test(password) &&
           /[a-z]/.test(password) &&
           /\d/.test(password) &&
           /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(password);
  };

  useEffect(() => {
    if (!token) {
      setError("Invalid reset link. Please request a new password reset.");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    
    if (!isPasswordValid()) {
      toast.error("Please create a stronger password");
      return;
    }
    
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });

      const data = await response.json();
      
      if (!response.ok) {
        if (data.detail?.error === 'password_invalid') {
          toast.error(data.detail.errors?.[0] || 'Password does not meet requirements');
        } else {
          throw new Error(data.detail || 'Failed to reset password');
        }
        return;
      }

      setSuccess(true);
    } catch (error) {
      toast.error(error.message);
      if (error.message.includes('expired') || error.message.includes('invalid')) {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Error state - invalid or expired token
  if (error) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-md animate-fade-in">
            <Link to="/" className="block text-center mb-12">
              <h1 
                className="text-3xl font-light text-[#2D2A26]"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                the soft ritual
              </h1>
            </Link>

            <div className="paper-card rounded-3xl p-8 md:p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-[#C8553D]/10 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-8 h-8 text-[#C8553D]" />
              </div>
              <h2 
                className="text-2xl font-normal text-[#2D2A26] mb-4"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Link expired
              </h2>
              <p className="text-[#6E6A66] font-light mb-6">
                {error}
              </p>
              <Link to="/forgot-password">
                <Button className="rounded-full px-6 bg-[#A87C6D] hover:bg-[#956d5f] text-white">
                  Request new link
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-md animate-fade-in">
            <Link to="/" className="block text-center mb-12">
              <h1 
                className="text-3xl font-light text-[#2D2A26]"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                the soft ritual
              </h1>
            </Link>

            <div className="paper-card rounded-3xl p-8 md:p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-[#7A9E7E]/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-[#7A9E7E]" />
              </div>
              <h2 
                className="text-2xl font-normal text-[#2D2A26] mb-4"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Password reset
              </h2>
              <p className="text-[#6E6A66] font-light mb-6">
                Your password has been successfully reset. You can now sign in with your new password.
              </p>
              <Link to="/login">
                <Button className="rounded-full px-6 bg-[#A87C6D] hover:bg-[#956d5f] text-white">
                  Sign in
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md animate-fade-in">
          <Link to="/" className="block text-center mb-12">
            <h1 
              className="text-3xl font-light text-[#2D2A26]"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              the soft ritual
            </h1>
          </Link>

          <div className="paper-card rounded-3xl p-8 md:p-10">
            <div className="w-14 h-14 rounded-full bg-[#A87C6D]/10 flex items-center justify-center mx-auto mb-6">
              <Lock className="w-7 h-7 text-[#A87C6D]" />
            </div>
            <h2 
              className="text-2xl font-normal text-[#2D2A26] text-center mb-2"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Create new password
            </h2>
            <p className="text-[#6E6A66] text-center font-light mb-8">
              Choose a strong password to keep your space secure.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#6E6A66] font-light">New password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a strong password"
                  required
                  className="rounded-xl py-6 border-[#E6E2DD] focus:border-[#A87C6D] focus:ring-[#A87C6D] bg-white"
                />
                <PasswordStrengthIndicator password={password} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-[#6E6A66] font-light">Confirm password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                  className={`rounded-xl py-6 border-[#E6E2DD] focus:border-[#A87C6D] focus:ring-[#A87C6D] bg-white ${
                    confirmPassword && password !== confirmPassword ? 'border-red-400' : ''
                  }`}
                />
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-red-500 text-xs">Passwords don't match</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading || !isPasswordValid() || password !== confirmPassword}
                className="w-full rounded-full py-6 bg-[#A87C6D] hover:bg-[#956d5f] text-white disabled:opacity-50"
              >
                {loading ? "Resetting..." : "Reset password"}
              </Button>
            </form>
          </div>

          <p className="text-center mt-8">
            <Link to="/login" className="text-[#A8A4A0] hover:text-[#6E6A66] text-sm">
              ← Back to sign in
            </Link>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
